import React, { useMemo } from "react";
import { motion } from "framer-motion";
import EntryContent from "./ui/EntryContent";
import EyebrowTitle from "./ui/EyebrowTitle";
import Button from "./ui/Button";
import SiteLink from "./ui/SiteLink";
import { extractPartnersData } from "../lib/utils";
import { PortableText } from "@portabletext/react";
import { portableTextUtils } from "./sanity/portableTextUtils";

import Section from "./ui/Section";
import { Satelite } from "../types/types";
import { url } from "inspector";

const Orbit = ({
  satelites,
  duration = 40,
  itemSize = "max(60px, 15vmin)",
  itemPadding = "0.5rem",
  scaleHover = 1.1,
  children,
}: {
  satelites: Satelite[];
  duration?: number;
  itemSize?: string;
  itemPadding?: string;
  scaleHover?: number;
  children?: React.ReactNode;
}) => {
  const satelitesUrlsPerLevels = useMemo(() => {
    return satelites.reduce((acc, satelite) => {
      if (acc[satelite.level]) {
        acc[satelite.level].push(satelite);
      } else {
        acc[satelite.level] = [satelite];
      }
      return acc;
    }, {}) as Record<string, Satelite[]>;
  }, [satelites]);

  const numberOfLevels = useMemo(
    () => Object.keys(satelitesUrlsPerLevels).length,
    [satelitesUrlsPerLevels]
  );

  return (
    <>
      {Object.keys(satelitesUrlsPerLevels).map((level, levelIndex) => {
        const satelitesInLevel = satelitesUrlsPerLevels[level];

        return (
          <div
            className="absolute inset-0 aspect-square rounded-full border border-gray-200"
            data-level={level}
            style={{
              margin: `calc(${itemSize} * ${numberOfLevels - levelIndex})`,
              zIndex: 20 - levelIndex,
            }}
            key={levelIndex}
          >
            {satelitesInLevel.map((satelite, sateliteIndex) => {
              return (
                <motion.div
                  key={sateliteIndex}
                  className="absolute left-1/2 right-0 top-1/2 select-none"
                  animate={{
                    rotate:
                      360 + (360 / satelitesInLevel.length) * sateliteIndex,
                  }}
                  initial={{
                    rotate: 0 + (360 / satelitesInLevel.length) * sateliteIndex,
                  }}
                  transition={{
                    duration: duration * (levelIndex + 1),
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  style={{ transformOrigin: "left center" }}
                >
                  <motion.div
                    className="absolute right-0 flex items-center justify-center rounded-full bg-white"
                    style={{
                      width: itemSize,
                      height: itemSize,
                      marginRight: `calc(${itemSize} / -2)`,
                      marginTop: `calc(${itemSize} / -2)`,
                      padding: itemPadding,
                    }}
                    animate={{
                      rotate:
                        -360 - (360 / satelitesInLevel.length) * sateliteIndex,
                    }}
                    initial={{
                      rotate:
                        0 + (-360 / satelitesInLevel.length) * sateliteIndex,
                    }}
                    transition={{
                      duration: duration * (levelIndex + 1),
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <motion.div
                      whileHover={{
                        scale: scaleHover,
                      }}
                      transition={{ type: "spring", stiffness: 200 }}
                      className="relative flex items-center justify-center overflow-hidden rounded-full bg-white shadow"
                    >
                      {satelite.url ? (
                        <SiteLink
                          to={satelite.url}
                          external
                          className="p-1 sm:p-4"
                        >
                          <img
                            src={satelite.imageSrc}
                            className="h-full w-full object-cover"
                            alt=""
                          />
                        </SiteLink>
                      ) : (
                        <img
                          src={satelite.imageSrc}
                          className="h-full w-full object-cover p-1 sm:p-4"
                          alt=""
                        />
                      )}
                    </motion.div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        );
      })}
      <div
        className="relative z-[21] flex aspect-square w-max flex-shrink-0 items-center justify-center sm:p-6"
        style={{
          margin: `calc((${itemSize} * ${numberOfLevels}) + ${itemSize})`,
        }}
      >
        <div>{children}</div>
      </div>
    </>
  );
};

const EcoSystem = ({ data }) => {
  const { badge, title, _rawDescription, cta, _rawPartners } = data;
  const satelitesUrls = extractPartnersData(_rawPartners.partnersList);
  return (
    <Section className="overflow-hidden !py-0">
      <div className="flex items-center justify-center">
        <div className="relative">
          <Orbit satelites={satelitesUrls} duration={40}>
            <div className="flex max-w-xs flex-col items-center justify-center text-center sm:max-w-lg ">
              {badge && <EyebrowTitle label={badge} as="p" />}
              <EntryContent className="text-center">
                <h2>{title}</h2>
                <PortableText
                  value={_rawDescription}
                  components={portableTextUtils}
                />
                {cta && (
                  <p>
                    <Button asChild size={"lg"}>
                      <SiteLink
                        external={cta.isExternal}
                        to={cta.link}
                        className="btn btn-primary"
                      >
                        {cta.label}
                      </SiteLink>
                    </Button>
                  </p>
                )}
              </EntryContent>
            </div>
          </Orbit>
        </div>
      </div>
    </Section>
  );
};

export default EcoSystem;
